// src/components/NotificationPanel.jsx
import { useEffect, useRef } from "react";
import { useNotificationPrefs, NOTIF_EVENT_TYPES } from "../hooks/useNotificationPrefs";

function formatTs(ts) {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const TYPE_ICON = {
  task_assigned:       "📋",
  post_status_changed: "🔄",
  comment_on_post:     "💬",
  comment_mention:     "@",
  systemic_alert:      "⚠",
  draft_task_created:  "📝",
  task_completed:      "✅",
};

export default function NotificationPanel({ uid, notifications, unreadCount, markRead, markAllRead, onClose }) {
  const { getPref, setPref } = useNotificationPrefs(uid);
  const panelRef = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onClose]);

  return (
    <div className="notif-panel" ref={panelRef}>
      <div className="notif-panel-header">
        <span className="notif-panel-title">Notifications</span>
        {unreadCount > 0 && (
          <button className="notif-mark-all-btn" onClick={markAllRead}>Mark all read</button>
        )}
        <button className="notif-close-btn" onClick={onClose} title="Close">×</button>
      </div>

      <div className="notif-list">
        {notifications.length === 0 ? (
          <div className="notif-empty">No notifications yet.</div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              className={`notif-item${n.read ? "" : " notif-unread"}`}
              onClick={() => { if (!n.read) markRead(n.id); }}
            >
              <span className="notif-icon">{TYPE_ICON[n.type] || "•"}</span>
              <div className="notif-body">
                <div className="notif-title">{n.title}</div>
                {n.body && <div className="notif-text">{n.body}</div>}
                <div className="notif-ts">{formatTs(n.createdAt)}</div>
              </div>
              {!n.read && <span className="notif-dot" />}
            </div>
          ))
        )}
      </div>

      <div className="notif-prefs-section">
        <div className="notif-prefs-title">Notification Preferences</div>
        {NOTIF_EVENT_TYPES.map(({ key, label }) => (
          <div key={key} className="notif-pref-row">
            <span className="notif-pref-label">{label}</span>
            <select
              className="notif-pref-select"
              value={getPref(key)}
              onChange={(e) => setPref(key, e.target.value)}
            >
              <option value="all">Push + In-app</option>
              <option value="in-app">In-app only</option>
              <option value="off">Off</option>
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
